import {DocumentStoreApi} from '@/components/document/store';
import {STATE_VALUE_WILDCARD_SUFFIX} from '@/constants/engine';
import {AccessRecord, DependencyMap, DocumentModel, ValueResolver} from '@/engine';
import {createConditionsReducer, createValidationsReducer, createEvents} from './reducers';

export const getKeysToUpdate = (
  changedKeys: string[],
  dependencyMap: DependencyMap,
  accessMap: Map<string, AccessRecord>,
  category: 'all' | 'visibility' = 'all',
) => {
  const keysToUpdate = new Set<string>();
  for (const changedKey of changedKeys) {
    if (!accessMap.has(changedKey)) {
      // If a field isn't part of the accessMap, we compute all the fields that depend on it instead
      for (const [key, dependencies] of dependencyMap.map) {
        const included = [...dependencies[category]].some(dep =>
          dep.includes(STATE_VALUE_WILDCARD_SUFFIX) ? changedKey.startsWith(dep.slice(0, -1)) : dep === changedKey,
        );

        if (included) {
          keysToUpdate.add(key);
        }
      }
    } else {
      keysToUpdate.add(changedKey);
    }
  }
  return [...keysToUpdate];
};

export const getDocumentProcessorUtils = (
  store: DocumentStoreApi,
  valueResolver: ValueResolver,
  document?: DocumentModel,
) => {
  const visibilitySet = document ? createVisibilitySet(document) : new Set<string>();

  return {
    computeConditions: createConditionsReducer(valueResolver, visibilitySet),
    computeValidations: createValidationsReducer(valueResolver, visibilitySet),
    computeEvents: createEvents(store, valueResolver, visibilitySet, document),
    store,
    valueResolver,
    visibilitySet,
    processedIds: new Set<string>(),
  };
};
export type DocumentProcessorUtils = ReturnType<typeof getDocumentProcessorUtils>;

export const createVisibilitySet = (document: DocumentModel): Set<string> => {
  return new Set(
    document.sections.flatMap(section => {
      if (!section.visible) return [];

      return [
        section.id,
        ...section.groups.flatMap(group => {
          if ('rows' in group) {
            return group.rows.flatMap(row => {
              if (!row.visible) return [];
              return [group.id, row.id, ...row.fields.filter(field => field.visible).map(field => field.id)];
            });
          } else {
            if (!group.visible) return [];
            return [group.id, ...group.fields.filter(field => field.visible).map(field => field.id)];
          }
        }),
      ];
    }),
  );
};
